import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'

export interface TelegramSubscription {
  id: string
  telegramId: string
  telegramUsername: string | null
  firstName: string | null
  lastName: string | null
  status: 'pending' | 'approved' | 'rejected'
  userId: string | null
  createdAt: string
  updatedAt: string
}

// Types (apiClient already unwraps the { success, data } envelope)
interface SubscriptionsData {
  items: TelegramSubscription[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface SubscriptionStatsData {
  pending: number
  approved: number
  rejected: number
  total: number
}

interface BotStatusData {
  running: boolean
}

// Query keys
const telegramKeys = {
  all: ['telegram'] as const,
  subscriptions: (params?: { status?: string; page?: number }) =>
    [...telegramKeys.all, 'subscriptions', params] as const,
  stats: () => [...telegramKeys.all, 'stats'] as const,
  botStatus: () => [...telegramKeys.all, 'bot-status'] as const,
}

// Get subscriptions list
export function useTelegramSubscriptions(params?: { status?: string; page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: telegramKeys.subscriptions(params),
    queryFn: () => {
      const searchParams = new URLSearchParams()
      if (params?.status) searchParams.set('status', params.status)
      if (params?.page) searchParams.set('page', String(params.page))
      if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))

      const url = `/telegram/subscriptions${searchParams.toString() ? `?${searchParams}` : ''}`
      return apiClient<SubscriptionsData>(url)
    },
  })
}

// Get subscription stats
export function useTelegramStats() {
  return useQuery({
    queryKey: telegramKeys.stats(),
    queryFn: () => apiClient<SubscriptionStatsData>('/telegram/subscriptions/stats'),
  })
}

// Get bot status
export function useBotStatus() {
  return useQuery({
    queryKey: telegramKeys.botStatus(),
    queryFn: () => apiClient<BotStatusData>('/telegram/bot/status'),
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

// Approve subscription
export function useApproveSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiClient<TelegramSubscription>(`/telegram/subscriptions/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: telegramKeys.all })
    },
  })
}

// Reject subscription
export function useRejectSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiClient<TelegramSubscription>(`/telegram/subscriptions/${id}/reject`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: telegramKeys.all })
    },
  })
}

// Delete subscription
export function useDeleteSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiClient(`/telegram/subscriptions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: telegramKeys.all })
    },
  })
}
